import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getERPExecutionById, startExecutionStep, completeExecutionStep, skipExecutionStep, reportDeviation, reportIssue } from '../../../service/api';

const ExecutionTracker = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [execution, setExecution] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedStep, setSelectedStep] = useState<any>(null);
    const [showDeviationModal, setShowDeviationModal] = useState(false);
    const [showIssueModal, setShowIssueModal] = useState(false);

    useEffect(() => {
        if (!id) return;
        fetchExecutionDetails();
        const interval = setInterval(fetchExecutionDetails, 10000); // Poll every 10 seconds
        return () => clearInterval(interval);
    }, [id]);

    const fetchExecutionDetails = async () => {
        if (!id) return;
        try {
            setLoading(false); // Only show loading on first fetch
            const response = await getERPExecutionById(id);
            if (response.data.success) {
                setExecution(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching execution details:', error);
        }
    };

    const handleStartStep = async (stepNumber: number) => {
        if (!id) return;
        try {
            const response = await startExecutionStep(id, stepNumber);
            if (response.data.success) {
                setExecution(response.data.data);
            }
        } catch (error) {
            console.error('Error starting step:', error);
            alert('Failed to start step');
        }
    };

    const handleCompleteStep = async (stepNumber: number, notes?: string) => {
        if (!id) return;
        try {
            const response = await completeExecutionStep(id, stepNumber, notes);
            if (response.data.success) {
                setExecution(response.data.data);
            }
        } catch (error) {
            console.error('Error completing step:', error);
            alert('Failed to complete step');
        }
    };

    const handleSkipStep = async (stepNumber: number, skipReason: string) => {
        if (!id) return;
        try {
            const response = await skipExecutionStep(id, stepNumber, skipReason);
            if (response.data.success) {
                setExecution(response.data.data);
            }
        } catch (error) {
            console.error('Error skipping step:', error);
            alert('Failed to skip step');
        }
    };

    const handleReportDeviation = async (data: any) => {
        if (!id || !selectedStep) return;
        try {
            const response = await reportDeviation(id, selectedStep.stepNumber, data);
            if (response.data.success) {
                setExecution(response.data.data);
                setShowDeviationModal(false);
                setSelectedStep(null);
            }
        } catch (error) {
            console.error('Error reporting deviation:', error);
            alert('Failed to report deviation');
        }
    };

    const handleReportIssue = async (data: any) => {
        if (!id || !selectedStep) return;
        try {
            const response = await reportIssue(id, selectedStep.stepNumber, data);
            if (response.data.success) {
                setExecution(response.data.data);
                setShowIssueModal(false);
                setSelectedStep(null);
            }
        } catch (error) {
            console.error('Error reporting issue:', error);
            alert('Failed to report issue');
        }
    };

    if (loading || !execution) {
        return <div style={{ padding: '24px' }}>Loading...</div>;
    }

    const statusColors = {
        Initiated: '#3b82f6',
        'In Progress': '#f59e0b',
        Completed: '#10b981',
        Aborted: '#dc2626'
    };

    const stepStatusColors = {
        Pending: '#6b7280',
        'In Progress': '#f59e0b',
        Completed: '#10b981',
        Skipped: '#9ca3af'
    };

    const calculateElapsedTime = () => {
        const start = new Date(execution.triggeredAt);
        const end = execution.completedAt ? new Date(execution.completedAt) : new Date();
        const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60)); // minutes
        return diff;
    };

    const elapsedTime = calculateElapsedTime();
    const slaRemaining = execution.overallSLA.expected - elapsedTime;

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            <button
                onClick={() => navigate('/external-emergency-preparedness/erp-executions')}
                style={{
                    marginBottom: '20px',
                    padding: '8px 16px',
                    backgroundColor: '#e5e7eb',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                }}
            >
                ← Back to Executions
            </button>

            {/* Header */}
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                            {execution.erpId?.planName || execution.bcpId?.planName || 'Emergency Response Execution'}
                        </h1>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>
                            Execution ID: {execution.executionId} | Triggered: {new Date(execution.triggeredAt).toLocaleString()}
                        </p>
                    </div>
                    <span style={{
                        padding: '6px 16px',
                        borderRadius: '12px',
                        backgroundColor: (statusColors as any)[execution.status] + '20',
                        color: (statusColors as any)[execution.status],
                        fontWeight: 'bold'
                    }}>
                        {execution.status}
                    </span>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Overall Progress</span>
                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#3b82f6' }}>
                            {execution.progressPercentage}%
                        </span>
                    </div>
                    <div style={{
                        width: '100%',
                        height: '12px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '6px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${execution.progressPercentage}%`,
                            height: '100%',
                            backgroundColor: execution.progressPercentage === 100 ? '#10b981' : '#3b82f6',
                            transition: 'width 0.3s'
                        }} />
                    </div>
                </div>

                {/* Stats Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Steps</p>
                        <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{execution.steps.length}</p>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Completed</p>
                        <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>
                            {execution.steps.filter((s: any) => s.status === 'Completed').length}
                        </p>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>SLA Remaining</p>
                        <p style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: slaRemaining < 0 ? '#dc2626' : slaRemaining < 30 ? '#f59e0b' : '#10b981'
                        }}>
                            {slaRemaining} min
                        </p>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Deviations</p>
                        <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#ea580c' }}>
                            {execution.totalDeviations || 0}
                        </p>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Issues</p>
                        <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc2626' }}>
                            {execution.totalIssues || 0}
                        </p>
                    </div>
                </div>
            </div>

            {/* Steps Timeline */}
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>Execution Steps</h2>

                <div style={{ position: 'relative' }}>
                    {/* Timeline Line */}
                    <div style={{
                        position: 'absolute',
                        left: '20px',
                        top: '30px',
                        bottom: '30px',
                        width: '2px',
                        backgroundColor: '#e5e7eb'
                    }} />

                    {execution.steps.map((step: any, index: number) => (
                        <div key={step.stepNumber} style={{
                            position: 'relative',
                            marginBottom: '24px',
                            paddingLeft: '60px'
                        }}>
                            {/* Timeline Dot */}
                            <div style={{
                                position: 'absolute',
                                left: '10px',
                                top: '10px',
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                backgroundColor: (stepStatusColors as any)[step.status],
                                border: '3px solid white',
                                boxShadow: '0 0 0 2px ' + (stepStatusColors as any)[step.status]
                            }} />

                            {/* Step Card */}
                            <div style={{
                                padding: '16px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                                    <div>
                                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                                            Step {step.stepNumber}: {step.stepName}
                                        </h3>
                                        {step.description && (
                                            <p style={{ fontSize: '14px', color: '#6b7280' }}>{step.description}</p>
                                        )}
                                    </div>
                                    <span style={{
                                        padding: '4px 12px',
                                        borderRadius: '8px',
                                        backgroundColor: (stepStatusColors as any)[step.status] + '20',
                                        color: (stepStatusColors as any)[step.status],
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        {step.status}
                                    </span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
                                    <div>
                                        <p style={{ fontSize: '12px', color: '#6b7280' }}>Assigned To</p>
                                        <p style={{ fontSize: '14px', fontWeight: '500' }}>
                                            {step.assignedTo?.name || 'Unassigned'}
                                        </p>
                                    </div>
                                    <div>
                                        <p style={{ fontSize: '12px', color: '#6b7280' }}>Duration</p>
                                        <p style={{ fontSize: '14px', fontWeight: '500' }}>
                                            {step.actualDuration ? `${step.actualDuration} min` : '-'} / {step.expectedDuration} min
                                        </p>
                                    </div>
                                    <div>
                                        <p style={{ fontSize: '12px', color: '#6b7280' }}>Deviations / Issues</p>
                                        <p style={{ fontSize: '14px', fontWeight: '500' }}>
                                            <span style={{ color: '#ea580c' }}>{step.deviations.length}</span> / <span style={{ color: '#dc2626' }}>{step.issues.length}</span>
                                        </p>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {step.status === 'Pending' && (
                                        <button
                                            onClick={() => handleStartStep(step.stepNumber)}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: '#f59e0b',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Start Step
                                        </button>
                                    )}
                                    {step.status === 'In Progress' && (
                                        <>
                                            <button
                                                onClick={() => handleCompleteStep(step.stepNumber)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#10b981',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Complete Step
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const reason = prompt('Enter reason for skipping this step:');
                                                    if (reason) handleSkipStep(step.stepNumber, reason);
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#9ca3af',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Skip Step
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={() => {
                                            setSelectedStep(step);
                                            setShowDeviationModal(true);
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#ea580c',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Report Deviation
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedStep(step);
                                            setShowIssueModal(true);
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Report Issue
                                    </button>
                                </div>

                                {/* Notes */}
                                {step.notes && (
                                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '6px' }}>
                                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Notes:</p>
                                        <p style={{ fontSize: '14px' }}>{step.notes}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Deviation Modal */}
            {showDeviationModal && selectedStep && (
                <DeviationModal
                    step={selectedStep}
                    onSubmit={handleReportDeviation}
                    onClose={() => {
                        setShowDeviationModal(false);
                        setSelectedStep(null);
                    }}
                />
            )}

            {/* Issue Modal */}
            {showIssueModal && selectedStep && (
                <IssueModal
                    step={selectedStep}
                    onSubmit={handleReportIssue}
                    onClose={() => {
                        setShowIssueModal(false);
                        setSelectedStep(null);
                    }}
                />
            )}
        </div>
    );
};

// Deviation Modal Component
const DeviationModal = ({ step, onSubmit, onClose }: any) => {
    const [formData, setFormData] = useState({
        description: '',
        severity: 'Minor'
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({
            stepNumber: step.stepNumber,
            ...formData
        });
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '100%'
            }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Report Deviation - Step {step.stepNumber}
                </h3>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                            Severity
                        </label>
                        <select
                            value={formData.severity}
                            onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db'
                            }}
                        >
                            <option value="Minor">Minor</option>
                            <option value="Major">Major</option>
                            <option value="Critical">Critical</option>
                        </select>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                resize: 'vertical'
                            }}
                            required
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#e5e7eb',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#ea580c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Issue Modal Component
const IssueModal = ({ step, onSubmit, onClose }: any) => {
    const [formData, setFormData] = useState({
        description: '',
        resolution: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({
            stepNumber: step.stepNumber,
            ...formData
        });
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '100%'
            }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Report Issue - Step {step.stepNumber}
                </h3>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                resize: 'vertical'
                            }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                            Resolution (Optional)
                        </label>
                        <textarea
                            value={formData.resolution}
                            onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                resize: 'vertical'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#e5e7eb',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExecutionTracker;
